import { useRef } from 'react';

import * as S from 'components/charts/styled/StatisticsDataCards.styled';
import Icon from 'components/global/Icon';
import Skeleton from 'components/global/Skeleton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useLazyFetch from 'hooks/useLazyFetch';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faArrowDown, faArrowUp, faArrowsLeftRight } from '@fortawesome/pro-solid-svg-icons';

import { DataCardsWrapper } from './styled/DataCards.styled';

/**
 * @param {Object} props
 * @param {Object} props.body - The body of the fetch request.
 * @param {string} props.fetchURL - The URL of the fetch request.
 * @param {number} [props.itemsPerRow = 3] - The number of items per row.
 * @param {Object[]} [props.cardConfigs = []] - The configurations of the cards.
 * @param {string} props.cardConfigs[].accessor - The accessor of the data.
 * @param {string} props.cardConfigs[].icon - The icon of the card.
 * @param {string} props.cardConfigs[].label - The label of the card.
 * @returns {JSX.Element}
 * @example
 * <DashboardDataCards
 * 	body={body}
 * 	fetchURL={REPORT.OTO.GET_COMPARED_ORDER_WEEKLY}
 * 		cardConfigs={[
 * 			{
 * 				accessor: 'thisWeek',
 * 				icon: 'fa-regular fa-calendar',
 * 				label: 'dashboard.statistic.thisWeek'
 * 				tooltip: true
 * 			}
 * 	]}/>
 *
 */

const StatisticsDataCards = ({ body, fetchURL, cardConfigs = [] }) => {
	const ref = useRef();
	const { data, loading } = useLazyFetch({ url: fetchURL, body, triggerElement: ref });

	return (
		<DataCardsWrapper ref={ref}>
			{cardConfigs?.map((config, index) => (
				<StatCard config={config} data={data} loading={loading} key={index} />
			))}
		</DataCardsWrapper>
	);
};

const StatCard = ({ loading, config, data: dataProp, index }) => {
	const abs = (num) => (parseInt(num) ? Math.abs(num) : 0);

	const formatNumber = (num) => abs(num).toLocaleString();
	const data = dataProp?.[config?.accessor];

	return loading ? (
		<S.CardContainer key={index}>
			<S.Container>
				<S.LeftCol>
					<Skeleton width="5rem" height="5rem" borderRadius="var(--small-border-radius)" />
				</S.LeftCol>
				<S.RightCol>
					<Skeleton width="15rem" height="2.4rem" />
					<Skeleton width="8rem" height="2.6rem" />
				</S.RightCol>
			</S.Container>
			<S.BottomContainer>
				<Skeleton width="8rem" height="2rem" />
			</S.BottomContainer>
		</S.CardContainer>
	) : (
		<S.CardContainer key={index}>
			<S.Container>
				<S.LeftCol>
					<Icon kitName={config?.customIcon} icon={config?.icon} size="medium" />
				</S.LeftCol>
				<S.RightCol>
					<div>
						<Typography variant="p2" gutterBottom>
							{config?.label}
						</Typography>

						{config?.tooltip && (
							<S.TooltipContainer>
								<Tooltip placement="right" title={`dashboard.${config.accessor}.description`}>
									<Icon icon={faCircleInfo} />
								</Tooltip>
							</S.TooltipContainer>
						)}
					</div>

					<Typography
						variant="h4"
						fontWeight="700"
						{...(data?.currency && {
							withCurrency: !!data?.currency,
							currency: data?.currency
						})}>
						{data?.currency ? data?.value : formatNumber(data?.value)}
					</Typography>

					{config?.onShowFooter?.(data)}
				</S.RightCol>
			</S.Container>

			{data?.diff !== undefined && data?.percent !== undefined && (
				<S.BottomContainer>
					<S.IconContainer>
						<Icon
							icon={getIconAndColor(data)?.icon}
							style={{
								color: getIconAndColor(data)?.color
							}}
						/>
					</S.IconContainer>

					<Typography
						color={getIconAndColor(data)?.color}
						fontWeight="500"
						fontSize="1.6rem"
						{...(data?.currency && {
							withCurrency: !!data?.currency,
							currency: data?.currency
						})}>
						{data?.currency ? abs(data?.diff) : formatNumber(data?.diff)}
					</Typography>

					<Typography color={getIconAndColor(data)?.color} fontWeight="500" fontSize="1.6rem">
						({Math.abs(Number(data?.percent)).toFixed(2)}%)
					</Typography>
				</S.BottomContainer>
			)}
		</S.CardContainer>
	);
};

const getIconAndColor = (data) => {
	if (data?.value > data?.diff && data?.diff >= 0) {
		return { icon: faArrowUp, color: 'var(--dataCardPossitiveColor)' };
	}
	if (data?.value === data?.diff) {
		return { icon: faArrowsLeftRight, color: 'var(--content-3)' };
	}
	{
		return { icon: faArrowDown, color: 'var(--dataCardNegativeColor)' };
	}
};

export default StatisticsDataCards;
